import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Globe } from 'lucide-react';
import { contactInfo as defaultContactInfo } from '../data';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent } from '../context/CmsImagesContext';

const navLinks = [
  { 
    name: { da: 'Hjem', en: 'Home' }, 
    path: '/' 
  },
  { 
    name: { da: 'Tjenester', en: 'Services' }, 
    path: '/tjenester' 
  },
  { 
    name: { da: 'Erhverv', en: 'Business' }, 
    path: '/erhvervsrengoring' 
  },
  { 
    name: { da: 'Privat', en: 'Private' }, 
    path: '/privatrengoring' 
  },
  { 
    name: { da: 'Galleri', en: 'Gallery' }, 
    path: '/galleri' 
  },
  { 
    name: { da: 'Om os', en: 'About' }, 
    path: '/om-os' 
  },
  { 
    name: { da: 'Nyheder', en: 'News' }, 
    path: '/nyheder' 
  },
  { 
    name: { da: 'Kontakt', en: 'Contact' }, 
    path: '/kontakt' 
  },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { getImage, companyInfo } = useCmsContent();
  const contactInfo = companyInfo || defaultContactInfo;

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav className="fixed w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src={getImage('logo', '/images/ftr_logo_v1.png', { width: 300 })} 
                alt="FTR Service Logo" 
                className="h-20 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#85d7f9] ${
                  isActive(link.path) ? 'text-brand-primary' : 'text-gray-600'
                }`}
              >
                {t(link.name)}
              </Link>
            ))}
            
            <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-gray-100">
              <div className="flex items-center bg-gray-50 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('da')}
                  className={`p-1 rounded-md transition-all flex items-center justify-center ${
                    language === 'da'
                      ? 'bg-white shadow-sm ring-1 ring-gray-100'
                      : 'opacity-50 hover:opacity-100'
                  }`}
                  aria-label="Dansk"
                >
                  <svg className="w-5 h-3.5 rounded-xs" viewBox="0 0 370 280">
                    <rect width="370" height="280" fill="#C8102E"/>
                    <rect x="120" width="40" height="280" fill="#FFFFFF"/>
                    <rect y="120" width="370" height="40" fill="#FFFFFF"/>
                  </svg>
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`p-1 rounded-md transition-all flex items-center justify-center ${
                    language === 'en'
                      ? 'bg-white shadow-sm ring-1 ring-gray-100'
                      : 'opacity-50 hover:opacity-100'
                  }`}
                  aria-label="English"
                >
                  <svg className="w-5 h-3.5 rounded-xs" viewBox="0 0 50 30">
                    <rect width="50" height="30" fill="#012169" />
                    <path d="M0,0 L50,30 M0,30 L50,0" stroke="#fff" strokeWidth="6" />
                    <path d="M0,0 L50,30 M0,30 L50,0" stroke="#C8102E" strokeWidth="3" />
                    <path d="M25,0 V30 M0,15 H50" stroke="#fff" strokeWidth="10" />
                    <path d="M25,0 V30 M0,15 H50" stroke="#C8102E" strokeWidth="6" />
                  </svg>
                </button>
              </div>

              <a 
                href={contactInfo.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#1877F2] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <Link
                to="/kontakt"
                className="bg-[#85d7f9] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-all shadow-sm shadow-[#85d7f9]/20"
              >
                {t({ da: 'Få et tilbud', en: 'Get a quote' })}
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <div className="flex items-center bg-gray-50 rounded-lg p-1">
              <button
                onClick={() => setLanguage('da')}
                className={`px-2 py-1 text-xs font-bold rounded-md transition-all ${
                  language === 'da'
                    ? 'bg-white text-[#85d7f9] shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                DA
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 text-xs font-bold rounded-md transition-all ${
                  language === 'en'
                    ? 'bg-white text-[#85d7f9] shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                EN
              </button>
            </div>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-brand-primary p-2"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-b border-gray-100 animate-in slide-in-from-top duration-300">
          <div className="px-4 pt-2 pb-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-4 text-base font-medium rounded-md ${
                  isActive(link.path)
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-gray-600 hover:text-brand-primary hover:bg-gray-50'
                }`}
              >
                {t(link.name)}
              </Link>
            ))}
            <div className="pt-4 flex flex-col space-y-4">
              <a 
                href={contactInfo.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 text-gray-600 hover:text-[#1877F2] py-2"
              >
                <Facebook className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {t({ da: 'Følg os på Facebook', en: 'Follow us on Facebook' })}
                </span>
              </a>
              <Link
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className="block w-full text-center bg-[#85d7f9] text-white px-5 py-3 rounded-xl text-base font-semibold"
              >
                {t({ da: 'Kontakt os', en: 'Contact us' })}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
